/**
 * AddTenantModal Component
 *
 * Modal dialog for adding a tenant to an organization.
 * Also allows granting tenant access to selected organization users at the same time.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Dropdown,
  Option,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../common/AsyncButton';
import { Alert } from '../common/Alert';
import { DualListPicker } from '../common/DualListPicker';
import { OrganizationApiService } from '../../services/organizationApi';
import type { OrganizationUser, OrganizationTenant } from '../../types/organization';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptyMessage: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

interface AddTenantModalProps {
  open: boolean;
  orgId: number;
  unassignedTenants: OrganizationTenant[];
  orgUsers: OrganizationUser[];
  onClose: () => void;
  onSuccess: () => void;
}

export function AddTenantModal({
  open,
  orgId,
  unassignedTenants,
  orgUsers,
  onClose,
  onSuccess,
}: AddTenantModalProps) {
  const styles = useStyles();

  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedTenantId(null);
      setSelectedUserIds([]);
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  // Get the selected tenant object
  const selectedTenant = unassignedTenants.find((t) => t.id === selectedTenantId);

  // Convert users to DualListPicker format
  const userItems = orgUsers.map((user) => ({
    id: user.id.toString(),
    label: user.name,
  }));

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedTenantId || !selectedTenant) return;

    setError(null);
    setIsSubmitting(true);

    try {
      // First, assign the tenant to the organization
      await OrganizationApiService.assignTenantToOrganization(orgId, selectedTenantId);

      // Then, grant access to selected users
      if (selectedUserIds.length > 0) {
        const updatePromises = selectedUserIds.map(async (userIdStr) => {
          const userId = parseInt(userIdStr, 10);
          // Find the user to get their existing tenant access
          const user = orgUsers.find((u) => u.id === userId);
          if (!user) return;

          // Add the new tenant to their existing access
          const existingAccess = user.accessible_tenant_ids || [];
          const newAccess = [...existingAccess, selectedTenant.tenant_id];

          await OrganizationApiService.updateUserTenantAccess(orgId, userId, newAccess);
        });

        await Promise.all(updatePromises);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add Tenant to Organization</DialogTitle>
          <DialogContent className={styles.content}>
            {error && <Alert intent="error">{error}</Alert>}

            {/* Tenant Selection */}
            {unassignedTenants.length === 0 ? (
              <Text className={styles.emptyMessage}>No unassigned tenants available.</Text>
            ) : (
              <Dropdown
                placeholder="Select a tenant..."
                onOptionSelect={(_event, data) => {
                  if (data.optionValue) {
                    setSelectedTenantId(parseInt(data.optionValue, 10));
                  }
                }}
                style={{ width: '100%' }}
              >
                {unassignedTenants.map((tenant) => (
                  <Option
                    key={tenant.id}
                    value={tenant.id.toString()}
                    text={`${tenant.tenant_name} (${tenant.domain || tenant.tenant_id})`}
                  >
                    {tenant.tenant_name} ({tenant.domain || tenant.tenant_id})
                  </Option>
                ))}
              </Dropdown>
            )}

            {/* User Access Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Text className={styles.sectionTitle}>Grant Access to Users</Text>
              </div>
              {orgUsers.length === 0 ? (
                <Text className={styles.emptyMessage}>No users in this organization.</Text>
              ) : (
                <DualListPicker
                  availableItems={userItems}
                  selectedIds={selectedUserIds}
                  onSelectionChange={setSelectedUserIds}
                  availableLabel="Available Users"
                  selectedLabel="Selected Users"
                />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleSubmit}
              disabled={!selectedTenantId || isSubmitting}
              loading={isSubmitting}
              loadingText="Adding..."
            >
              Add Tenant
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
